import { isAxiosError } from 'axios'
import type { Booking } from 'repositories/BerthApi/types/booking'
import type {
  BookingMasterData,
  TenantParameter,
} from 'repositories/BerthApi/getBookingMasterData/type'

import { BOOKING_STATUS } from 'constants/Booking'
import { BerthCard } from 'containers/SpBerthManagement/types'

const DEFAULT_ERROR_MESSAGE = 'ステータス更新に失敗しました。'

/**
 * 編集スライドの起動可否（受付済ステータスベース）。
 *
 * PC 版 `NeutralBookingItem/index.js:220-224` のダブルクリック attach 条件のうち、
 * Finish ステータスを除いた部分:
 *   isPreAssign || !isWaiting
 * - isPreAssign: generalBookingId が存在し、status === Unassigned
 * - isWaiting: generalBookingWaitingStatus が 1 (WAITING_ONE) または 2 (WAITING_TWO)
 *
 * SP では編集ボタン (鉛筆) の表示可否に使う。Finish の編集は SP 側で許容する。
 */
export const canEditBookingByStatus = (booking: Booking): boolean => {
  const isPreAssign =
    booking.generalBookingId != null && booking.status === BOOKING_STATUS.Unassigned
  const isWaiting =
    booking.generalBookingWaitingStatus === 1 || booking.generalBookingWaitingStatus === 2
  return isPreAssign || !isWaiting
}

export const getApiErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown })?.message
    if (typeof message === 'string' && message) return message
  }
  return DEFAULT_ERROR_MESSAGE
}

/**
 * planStartDate (YYYYMMDD) + planStartTime (HH:mm:ss) → "予:HH:mm MM/DD"
 * PC版の getPlanStartDateTime() に相当
 */
export const formatPlanStartDatetime = (b: Booking): string | undefined => {
  // 繰り返し予約でBooking作成前の場合は start_date/start_time を使用 (PC版ロジック準拠)
  let sourceDate = b.planStartDate
  let sourceTime = b.planStartTime
  if (!sourceDate && !sourceTime && b.repeatType !== null) {
    sourceDate = b.startDate
    sourceTime = b.startTime
  }
  if (!sourceDate && !sourceTime) return undefined
  const displayDate = sourceDate
    ? `${sourceDate.substring(4, 6)}/${sourceDate.substring(6, 8)}`
    : '未定'
  const displayTime = sourceTime ? sourceTime.substring(0, 5) : '未定'
  return `予:${displayTime} ${displayDate}`
}

/**
 * entryInstructionTime (yyyy-MM-dd HH:mm) → "指:HH:mm MM/DD"
 * PC版の getEntryInstructionTime() に相当
 */
export const formatEntryInstructionTime = (b: Booking): string | undefined => {
  const eit = b.entryInstructionTime
  if (!eit) return undefined
  const [datePart, timePart] = eit.split(' ')
  const parts = datePart?.split('-')
  const displayDate = parts?.length === 3 ? `${parts[1]}/${parts[2]}` : '未定'
  return `指:${timePart ?? '未定'} ${displayDate}`
}

/**
 * column_name（snake_case）に対応する表示値を解決する
 * PC版の renderTextLabel() に相当
 */
export const resolveColumnValue = (
  columnName: string,
  b: Booking,
  masterData: BookingMasterData
): string => {
  // company_name → 運送会社名 + 車番 (PC版準拠)
  if (columnName === 'company_name') {
    const company = b.driverCompanyName || '未定'
    const vn = b.vehicleNumber ? `[${b.vehicleNumber}]` : '未定'
    return `${company} ${vn}`
  }

  if (columnName === 'vehicle_number') {
    return b.vehicleNumber || ''
  }

  // vehicle_category → マスターデータから名称解決 (PC版: parameter_id でマッチ)
  if (columnName === 'vehicle_category') {
    const cat = masterData.vehicleCategories.find(
      (c) => String(c.parameterId) === String(b.vehicleCategory)
    )
    return cat?.parameterName || ''
  }

  if (columnName === 'plan_start_datetime') {
    return formatPlanStartDatetime(b) || ''
  }

  if (columnName === 'entry_instruction_time') {
    return formatEntryInstructionTime(b) || ''
  }

  // import/export package type → import優先、なければexportにフォールバック (PC版準拠)
  if (columnName === 'import_package_type' || columnName === 'export_package_type') {
    const importPkg = masterData.packageTypes.find(
      (p) => String(p.parameterId) === String(b.importPackageType)
    )
    const exportPkg = masterData.packageTypes.find(
      (p) => String(p.parameterId) === String(b.exportPackageType)
    )
    return importPkg?.parameterName || exportPkg?.parameterName || ''
  }

  // import/export additional columns (import_additional_column_1 等)
  const importMatch = columnName.match(/^import_additional_column_?(\d+)$/)
  if (importMatch) {
    const key = `importAdditionalColumn${importMatch[1]}` as keyof Booking
    const val = b[key]
    return val != null ? String(val) : ''
  }
  const exportMatch = columnName.match(/^export_additional_column_?(\d+)$/)
  if (exportMatch) {
    const key = `exportAdditionalColumn${exportMatch[1]}` as keyof Booking
    const val = b[key]
    return val != null ? String(val) : ''
  }

  // その他: snake_case → camelCase に変換してブッキングフィールドを参照
  const camelKey = columnName.replace(/_([a-z])/g, (_, c: string) =>
    c.toUpperCase()
  ) as keyof Booking
  const value = b[camelKey]
  return value != null ? String(value) : ''
}

/**
 * PC版 getDataImportExport() 相当
 * booking の importFlag / exportFlag に応じて import or export パラメータを返す
 */
const getImportExportParams = (b: Booking, masterData: BookingMasterData): TenantParameter[] => {
  const { importFlag, exportFlag } = b
  if (importFlag === 1 && exportFlag === 1) {
    // 両方チェック: import パラメータを使い、データがなければ columnName を export に差し替え
    return masterData.tenantParameters.import.map((param) => {
      const camelKey = param.columnName.replace(/_([a-z])/g, (_, c: string) =>
        c.toUpperCase()
      ) as keyof Booking
      if (b[camelKey]) return param
      return {
        ...param,
        columnName: param.columnName.replace('import', 'export'),
      }
    })
  }
  if (importFlag === 0 && exportFlag === 1) {
    return masterData.tenantParameters.export
  }
  if (importFlag === 1 && exportFlag === 0) {
    return masterData.tenantParameters.import
  }
  return []
}

/**
 * PC版 card_view_order（1〜6）に対応した表示値配列を計算する
 * テナント設定に基づいてどのフィールドをどの位置に表示するか決定
 */
export const computeCardViewItems = (
  b: Booking,
  masterData: BookingMasterData | null
): BerthCard['cardViewItems'] => {
  const items: BerthCard['cardViewItems'] = [
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
  ]
  if (!masterData) return items

  const importExportParams = getImportExportParams(b, masterData)
  const allParams = [...masterData.tenantParameters.base, ...importExportParams]

  for (const param of allParams) {
    const order = param.cardViewOrder
    if (order && order >= 1 && order <= 6) {
      const value = resolveColumnValue(param.columnName, b, masterData)
      if (value) items[order - 1] = value
    }
  }

  return items
}

/**
 * 表示日にまたがる予約の startTime/endTime を表示日の範囲にクリッピングする
 * PC版 handleFormatItem() に相当するロジック
 *
 * @param b - クリッピング対象の予約
 * @param displayDate - 表示対象日 (YYYYMMDD)
 */
export const clipBookingToDate = (b: Booking, displayDate: string): Booking => {
  const { startDate, endDate, endTime } = b
  if (!startDate || !endDate || startDate === endDate) return b

  if (displayDate === startDate) {
    // 表示日が開始日 → 終了時刻を当日末 (24:00:00) に切り詰める
    return { ...b, endTime: '24:00:00' }
  }
  if (displayDate === endDate && endTime !== '00:00:00') {
    // 表示日が終了日 → 開始時刻を当日始 (00:00:00) に切り詰める
    return { ...b, startTime: '00:00:00' }
  }
  if (displayDate !== startDate && displayDate !== endDate) {
    // 表示日が中間日 → 終日表示
    return { ...b, startTime: '00:00:00', endTime: '24:00:00' }
  }
  return b
}

/** bookingId が null の繰り返し予約用にフォールバックIDを生成 */
export const getBookingKey = (b: Booking): string =>
  b.bookingId ?? `repeat_${b.repeatId ?? ''}_${b.startDate ?? ''}`

/** Booking → BerthCard への変換 */
export const toCard = (b: Booking, masterData: BookingMasterData | null): BerthCard => {
  const importFlag = b.importFlag === 1
  const exportFlag = b.exportFlag === 1
  let cardType: string | undefined
  if (importFlag && exportFlag) cardType = '卸積'
  else if (importFlag) cardType = '卸'
  else if (exportFlag) cardType = '積'

  let callingStatus: string | undefined
  if (b.callingTime && !b.cancelCallingTime) {
    callingStatus = '呼出中'
  }

  return {
    id: getBookingKey(b),
    status: b.status ?? BOOKING_STATUS.Booking, // NOTE: 繰り返し予約は返却値にstatusがないため、便宜的にBookingと同じステータスを割り当てる
    startTime: b.startTime ?? '',
    endTime: b.endTime ?? '',
    title: b.driverCompanyName ?? '未定',
    companyName: b.driverCompanyName ?? undefined,
    vehicleNumber: b.vehicleNumber ?? undefined,
    assigned: b.status !== BOOKING_STATUS.Unassigned,
    cardType,
    vehicleType: b.vehicleCategory ?? undefined,
    shipper: b.importGoodsOwner ?? b.exportGoodsOwner ?? undefined,
    cargoType: b.importMainGoods ?? b.exportMainGoods ?? undefined,
    memo: b.adminNote ?? undefined,
    callingStatus,
    checkinDate: b.checkinDate ?? undefined,
    planStartDatetime: formatPlanStartDatetime(b),
    entryInstructionTime: formatEntryInstructionTime(b),
    importPackageTypeId: b.importPackageType ?? undefined,
    exportPackageTypeId: b.exportPackageType ?? undefined,
    portalFlag: b.portalFlag ?? undefined,
    lineFlag: b.lineFlag ?? undefined,
    isSeenFlag: b.isSeenFlag ?? undefined,
    checkinDateOfGeneralBooking: b.checkinDateOfGeneralBooking ?? undefined,
    generalBookingId: b.generalBookingId ?? undefined,
    generalBookingWaitingStatus: b.generalBookingWaitingStatus ?? undefined,
    cardViewItems: computeCardViewItems(b, masterData),
  }
}
